/**
 * SEO utility — updates document title, meta description, canonical, and OG tags
 * per page and per language. No external dependencies required.
 */

const BASE_URL = 'https://www.nexuni.com';

const PAGE_SEO = {
  home: {
    title: {
      zh: 'Nexuni 耐思尼 | AI 機器人自動化專家',
      en: 'Nexuni | AI Robots & Automation Solutions',
      ja: 'Nexuni | AI ロボット自動化ソリューション'
    },
    description: {
      zh: 'Nexuni 耐思尼為 NVIDIA Inception 與 Universal Robots 認證技術夥伴，已部署超過 100 套 AI 機器人與自動化系統，助企業節省 88% 人力成本。提供智慧焊接、自主巡邏機器人、高樓清潔機器人、雙臂服務機器人、停車管理等解決方案。',
      en: 'Nexuni is a certified NVIDIA Inception and Universal Robots technology partner with 100+ AI robot and automation systems deployed. We help businesses save 88% on labor costs with smart welding robots, security patrol dogs, facade cleaning robots, bimanual service robots, and parking management systems.',
      ja: 'Nexuni は NVIDIA Inception と Universal Robots の認定パートナー。100 以上の AI ロボット・自動化システムを導入済み。スマート溶接ロボット、警備巡回ロボットドッグ、外壁清掃ロボット、双腕サービスロボット、駐車管理システムで人件費 88% 削減を実現。'
    }
  },
  solutions: {
    title: {
      zh: 'Nexuni Robotics 耐思尼機器人 | AI 機器人產品與自動化方案',
      en: 'Nexuni Robotics | AI Robot Products & Automation Solutions',
      ja: 'Nexuni Robotics | AI ロボット製品・自動化ソリューション'
    },
    description: {
      zh: '探索 Nexuni Robotics 全系列 AI 機器人產品：智慧焊接機器人、自主巡邏機器人、高樓清潔機器人、AI 雙臂服務機器人、AI 智慧停車管理系統、AI 影像保全。可客製化或選購標準方案。',
      en: 'Explore Nexuni Robotics AI robot products: smart welding robot, security patrol dog, facade cleaning robot, AI bimanual service robot, LPR parking management, and AI video security. Standard catalog or custom Launch Customer solutions available.',
      ja: 'Nexuni Robotics AI ロボット製品：スマート溶接ロボット、警備巡回ロボットドッグ、外壁清掃ロボット、AI 双腕サービスロボット、車番認識駐車管理、AI 映像セキュリティ。標準製品またはカスタムソリューション。'
    }
  },
  about_us: {
    title: {
      zh: '關於我們 | Nexuni 耐思尼 — AI 機器人公司',
      en: 'About Us | Nexuni — AI Robotics Company',
      ja: '会社概要 | Nexuni — AI ロボット企業'
    },
    description: {
      zh: 'Nexuni 成立於新竹科學園區，為 NVIDIA Inception 與 Universal Robots 認證夥伴。團隊來自 MIT、CMU、台大、清大、陽明交大，掌握完整 AI 機器人核心技術，已於工廠、社區、停車場等超過 100 個場域穩定運行。',
      en: 'Nexuni, a certified NVIDIA Inception and Universal Robots partner founded in Hsinchu Science Park. Team from MIT, CMU, NTU, NTHU, NYCU with full AI robotics technology stack. Over 100 systems deployed across factories, communities, and parking facilities.',
      ja: 'Nexuni は NVIDIA Inception・Universal Robots 認定パートナー。新竹サイエンスパーク設立。MIT・CMU・台湾大学・清華大学・陽明交通大学出身チームが完全な AI ロボット技術を保有。工場・集合住宅・駐車場など 100 以上の場所で稼働中。'
    }
  },
  core_technology: {
    title: {
      zh: '核心技術 | Nexuni AI 機器人平台',
      en: 'Core Technology | Nexuni AI Robot Platform',
      ja: 'コア技術 | Nexuni AI ロボットプラットフォーム'
    },
    description: {
      zh: 'Nexuni 自研 AI 機器人核心平台：感知、控制、SLAM 定位與決策。強化學習實現四足與人形機器人運動控制，端到端 VLA 模型進行機械手臂操作，人類示範策略學習，支援多場域彈性部署。整合 NVIDIA AI 平台。',
      en: 'Nexuni proprietary AI robotics platform: perception, control, SLAM, and decision-making. Reinforcement learning for quadruped and humanoid locomotion, end-to-end VLA for robot manipulation, policy learning from human demonstration. Integrated with NVIDIA AI platform.',
      ja: 'Nexuni 独自 AI ロボットプラットフォーム：知覚・制御・SLAM・意思決定。強化学習による四足・ヒューマノイド歩行、エンドツーエンド VLA によるロボット操作、人間デモからの方策学習。NVIDIA AI プラットフォーム統合。'
    }
  },
  colab_flow: {
    title: {
      zh: '合作流程 | Nexuni — 標準合作與首發客戶兩種模式',
      en: 'How to Collaborate | Nexuni — Standard & Launch Customer Programs',
      ja: '協業プロセス | Nexuni — 標準導入 & ローンチカスタマー'
    },
    description: {
      zh: '兩種合作模式：標準合作 — 選擇產品、諮詢、報價、部署四步驟快速導入；首發客戶 — 簽署 MOU、前線部署、提案報價、設計開發到部署進化，從零打造專屬 AI 機器人自動化方案。',
      en: 'Two collaboration models: Standard — select, consult, quote, and deploy in four steps; Launch Customer — MOU signing, forward deployment, proposal with MOQ quote, custom design & development, and continuous evolution. Build your AI robot solution from scratch.',
      ja: '2つの協業モデル：標準導入 — 製品選定・相談・見積・導入の4ステップ；ローンチカスタマー — MOU締結・現場派遣・提案見積・設計開発・導入進化で専用 AI ロボットソリューションをゼロから構築。'
    }
  },
  career: {
    title: {
      zh: '加入我們 | Nexuni 耐思尼徵才',
      en: 'Careers at Nexuni | AI Robotics Jobs',
      ja: '採用情報 | Nexuni — AI ロボットエンジニア募集'
    },
    description: {
      zh: 'Nexuni 徵求機器人軟體工程師、AI 軟體工程師、硬體設計工程師與實習生。加入我們打造落地 AI 機器人，體驗團隊旅遊、技術交流的 Nexuni 生活。',
      en: 'Join Nexuni — hiring Robot Software Engineers (ROS2/SLAM), AI Engineers, Hardware Engineers, and interns. Build real-world AI robots in a collaborative culture with team trips, hands-on learning, and innovation.',
      ja: 'Nexuni 採用中 — ロボットソフトウェアエンジニア（ROS2/SLAM）、AI エンジニア、ハードウェアエンジニア、インターン募集。チーム旅行や技術交流の文化で実世界 AI ロボットを開発。'
    }
  },
  contact: {
    title: {
      zh: '聯絡我們 | Nexuni 耐思尼',
      en: 'Contact Us | Nexuni — AI Robot Solutions',
      ja: 'お問い合わせ | Nexuni'
    },
    description: {
      zh: '聯絡 Nexuni 耐思尼，了解 AI 機器人自動化解決方案與合作流程。NVIDIA Inception 與 Universal Robots 認證夥伴，新竹科學園區，服務台灣、新加坡及全球。',
      en: 'Contact Nexuni for AI robot automation solutions. NVIDIA Inception and Universal Robots certified partner. Based in Hsinchu Science Park, serving Taiwan, Singapore, and worldwide.',
      ja: 'Nexuni へのお問い合わせ。NVIDIA Inception・Universal Robots 認定パートナー。新竹サイエンスパーク拠点、台湾・シンガポール・世界各地にサービス提供。'
    }
  },
  stories: {
    title: {
      zh: '成功案例 | Nexuni 耐思尼',
      en: 'Case Studies | Nexuni AI Robots',
      ja: '導入事例 | Nexuni AI ロボット'
    },
    description: {
      zh: 'Nexuni AI 機器人成功案例：智慧停車場管理、自動化焊接產線、設施保全巡邏等真實導入成果。超過 100 套系統於工廠、社區、停車場穩定運行。',
      en: 'Nexuni AI robot case studies: smart parking management, automated welding lines, facility security patrol. Over 100 systems reliably operating across factories, communities, and parking facilities.',
      ja: 'Nexuni AI ロボット導入事例：スマート駐車場管理、自動溶接ライン、施設警備巡回。100 以上のシステムが工場・集合住宅・駐車場で安定稼働。'
    }
  },
  blog: {
    title: {
      zh: '最新消息 | Nexuni 耐思尼',
      en: 'News & Blog | Nexuni',
      ja: 'ニュース | Nexuni'
    },
    description: {
      zh: 'Nexuni 最新消息：產品發表、展覽活動、AI 機器人產業趨勢與合作夥伴動態。',
      en: 'Latest from Nexuni: product launches, exhibition events, AI robotics industry trends, and partnership updates.',
      ja: 'Nexuni 最新ニュース：製品発表、展示会イベント、AI ロボット業界動向、パートナーシップ情報。'
    }
  },
  environment: {
    title: {
      zh: '文化與環境 | Nexuni 耐思尼 — Life at Nexuni',
      en: 'Life at Nexuni | Culture & Work Environment',
      ja: '企業文化 | Nexuni — Life at Nexuni'
    },
    description: {
      zh: '了解 Nexuni 的企業文化：創新、實務學習、共同成長。團隊旅遊日本韓國、每月技術分享、機器人實作訓練，打造充滿活力的工作環境。',
      en: 'Discover Nexuni culture: innovation, hands-on learning, and growth. Team trips to Japan and Korea, monthly tech sharing, and robot training. A vibrant and empowering work environment.',
      ja: 'Nexuni の企業文化：革新、実践学習、共に成長。日本・韓国へのチーム旅行、月次技術共有、ロボット実践トレーニング。活気ある職場環境。'
    }
  }
};

const PRODUCT_SEO = {
  collie: {
    title: {
      zh: '自主巡邏機器人 | Nexuni',
      en: 'AI Security Patrol Robot Dog | Nexuni',
      ja: 'AI 警備巡回ロボットドッグ | Nexuni'
    },
    description: {
      zh: 'Nexuni 自主巡邏機器人，自主 SLAM 導航巡邏、即時異常偵測與告警，已部署於工廠、社區、園區。搭載 NVIDIA AI 平台，24/7 無人巡邏。',
      en: 'Nexuni AI security patrol robot dog — autonomous SLAM navigation, real-time anomaly detection, instant alerts. Deployed in factories, campuses, and communities. Powered by NVIDIA AI platform. 24/7 unmanned patrol.',
      ja: 'Nexuni AI 警備巡回ロボットドッグ。自律 SLAM ナビゲーション、リアルタイム異常検知、即時アラート。工場・キャンパス・コミュニティに導入済み。NVIDIA AI プラットフォーム搭載、24時間無人巡回。'
    }
  },
  beaver: {
    title: {
      zh: '智慧焊接機器人 | Nexuni',
      en: 'Smart Welding Robot | Nexuni',
      ja: 'スマート溶接ロボット | Nexuni'
    },
    description: {
      zh: 'Nexuni 智慧焊接機器人結合雷射掃描與 Universal Robots 協作手臂，自動修正工件誤差，設定時間減少 80%，生產效率提升 50%。適用金屬加工、製造業。',
      en: 'Nexuni smart welding robot combines laser scanning with Universal Robots collaborative arms. Auto-corrects workpiece deviations, reduces setup time by 80%, boosts efficiency by 50%. For metal fabrication and manufacturing.',
      ja: 'Nexuni スマート溶接ロボット。レーザースキャンと Universal Robots 協働アームを統合。ワーク誤差自動補正、段取り時間 80% 削減、生産効率 50% 向上。金属加工・製造業向け。'
    }
  },
  spider: {
    title: {
      zh: '高空作業機器人 | Nexuni',
      en: 'High-Rise Facade Cleaning Robot | Nexuni',
      ja: '高層外壁清掃ロボット | Nexuni'
    },
    description: {
      zh: 'Nexuni 高空作業機器人，AI 驅動自動化高危險作業，取代人工高空作業，提升安全與效率。',
      en: 'Nexuni high-rise facade cleaning robot — AI-powered automation replaces dangerous rope-access work, improving safety and efficiency in building facility management.',
      ja: 'Nexuni 高層外壁清掃ロボット。AI 駆動の自動化で危険なロープアクセス作業を代替、ビル施設管理の安全性と効率を向上。'
    }
  },
  elephant: {
    title: {
      zh: 'AI 雙臂式服務型機器人 | Nexuni',
      en: 'AI Bimanual Service Robot | Nexuni',
      ja: 'AI 双腕サービスロボット | Nexuni'
    },
    description: {
      zh: 'Nexuni AI 雙臂式服務型機器人，以端到端人類示範策略學習為核心，少量示範即可學會新任務，無需重新編程。適用工廠、倉儲、餐飲與服務業。',
      en: 'Nexuni AI bimanual service robot — learns new manipulation tasks from minimal human demos via end-to-end policy learning, no reprogramming needed. For factories, warehouses, food & beverage, and service industries.',
      ja: 'Nexuni AI 双腕サービスロボット。エンドツーエンド方策学習により少数の人間デモから新タスクを習得、再プログラミング不要。工場・倉庫・飲食業・サービス業向け。'
    }
  },
  pms100: {
    title: {
      zh: 'AI 智慧停車管理系統 | Nexuni',
      en: 'AI Parking Management System | Nexuni',
      ja: 'AI駐車管理システム | Nexuni'
    },
    description: {
      zh: 'Nexuni AI 智慧停車管理系統，AI 邊緣運算辨識，支援現金、信用卡、QR-CODE 支付，適用各類停車場智慧管理。',
      en: 'Nexuni LPR-based parking management system with AI edge computing, cash, credit card, and QR code payment. Smart parking automation for any facility.',
      ja: 'Nexuni AI駐車管理システム。AI エッジ推論、現金・クレジットカード・QR コード決済対応。あらゆる施設のスマート駐車場管理。'
    }
  },
  pms100m: {
    title: {
      zh: '社區車牌辨識停車管理主機 | Nexuni',
      en: 'Community LPR Parking Control Unit | Nexuni',
      ja: 'コミュニティ車番認識駐車管理ユニット | Nexuni'
    },
    description: {
      zh: 'Nexuni 社區車牌辨識停車管理主機，AI 車牌辨識、訪客管理、出入口控制三合一，體積小巧免汰換現有設備。',
      en: 'Nexuni community LPR parking control unit — compact AI license plate recognition, visitor management, and access control in one device. No infrastructure replacement needed.',
      ja: 'Nexuni コミュニティ車番認識駐車管理ユニット。コンパクトな AI 車番認識、訪問者管理、入退場制御を一体化。既存設備の交換不要。'
    }
  },
  pms100le: {
    title: {
      zh: '車牌辨識主機 | Nexuni',
      en: 'LPR Engine | Nexuni',
      ja: '車番認識エンジン | Nexuni'
    },
    description: {
      zh: 'Nexuni 車牌辨識主機，體積小於手機，低功耗 AI 邊緣運算，開放式 API 架構，輕鬆串接各類停車管理系統。',
      en: 'Nexuni LPR engine — smaller than a phone, low-power AI edge computing, open API architecture for seamless parking system integration.',
      ja: 'Nexuni 車番認識エンジン。スマホより小型、低消費電力 AI エッジ推論、オープン API で既存駐車システムに柔軟接続。'
    }
  },
  vpgs100: {
    title: {
      zh: '智慧影像車位導引系統 | Nexuni',
      en: 'Vision-Based Parking Guidance System | Nexuni',
      ja: '映像式駐車誘導システム | Nexuni'
    },
    description: {
      zh: 'Nexuni 智慧影像車位導引系統，AI 即時偵測空位並引導車輛，提升停車場營運效率與使用者體驗。',
      en: 'Nexuni vision-based parking guidance system uses AI to detect available spaces in real time, improving parking facility operations and user experience.',
      ja: 'Nexuni 映像式駐車誘導システム。AI がリアルタイムで空き状況を検知し、駐車場運営効率とユーザー体験を向上。'
    }
  },
  vs100: {
    title: {
      zh: 'AI 智慧影像保全系統 | Nexuni',
      en: 'AI Video Security System | Nexuni',
      ja: 'AI 映像セキュリティシステム | Nexuni'
    },
    description: {
      zh: 'Nexuni AI 智慧影像保全系統，多路攝影機即時監控、異常告警與智慧回放搜尋，大幅降低人力監看需求。',
      en: 'Nexuni AI video security system — multi-camera real-time monitoring, anomaly alerts, and smart playback search. Reduces manual surveillance needs.',
      ja: 'Nexuni AI 映像セキュリティシステム。マルチカメラのリアルタイム監視、異常アラート、スマート再生検索で人的監視を大幅削減。'
    }
  },
  ilp100: {
    title: {
      zh: 'AI 違停辨識系統 | Nexuni',
      en: 'AI Illegal Parking Detection System | Nexuni',
      ja: 'AI 違法駐車検知システム | Nexuni'
    },
    description: {
      zh: 'Nexuni AI 違停辨識系統，結合車牌辨識與 RF 定位技術，自動偵測違規停車並即時告警。',
      en: 'Nexuni AI illegal parking detection — LPR and RF positioning for automated violation detection and real-time alerts.',
      ja: 'Nexuni AI 違法駐車検知システム。車番認識と RF 測位を組み合わせた自動違反検知とリアルタイムアラート。'
    }
  },
  pp100: {
    title: {
      zh: '智慧停車柱 | Nexuni',
      en: 'Intelligent Parking Pole | Nexuni',
      ja: 'スマート駐車ポール | Nexuni'
    },
    description: {
      zh: 'Nexuni 智慧停車柱，搭載低功耗 AI 車牌辨識模組，可替換式電池設計，適合無電力供應場域。',
      en: 'Nexuni intelligent parking pole with low-power AI LPR module and replaceable battery. Ideal for locations without stable power supply.',
      ja: 'Nexuni スマート駐車ポール。低消費電力 AI 車番認識モジュール搭載、交換式バッテリー設計。電源のない場所に最適。'
    }
  }
};

/**
 * Convert internal lang code to URL slug.
 * 'zh' → 'zh-tw', others pass through.
 */
function langToSlug(lang) {
  if (lang === 'zh' || lang === 'zh-tw') return 'zh-tw';
  return lang;
}

/**
 * Update page SEO: title, description, canonical, OG tags, and html lang.
 * @param {string} pageKey - Key in PAGE_SEO (e.g. 'home', 'solutions')
 * @param {string} lang - Internal lang code ('zh', 'en', 'ja') or slug ('zh-tw')
 * @param {string} routePath - Route path WITHOUT lang prefix (e.g. '/', '/solutions', '/about/about_us')
 */
function updateSEO(pageKey, lang, routePath) {
  const l = (lang === 'zh-tw' || lang === 'zh') ? 'zh' : lang;
  const slug = langToSlug(lang);
  const seo = PAGE_SEO[pageKey] || PAGE_SEO['home'];

  // HTML lang attribute (critical for Googlebot language detection)
  var htmlLang = l === 'zh' ? 'zh-TW' : l;
  document.documentElement.setAttribute('lang', htmlLang);

  // Title
  document.title = seo.title[l] || seo.title['zh'];

  // Meta description
  setMeta('description', seo.description[l] || seo.description['zh']);

  // Canonical — always uses correct slug in URL
  const canonical = BASE_URL + '/' + slug + routePath;
  setLink('canonical', canonical);

  // Open Graph
  setMeta('og:title', document.title, 'property');
  setMeta('og:description', seo.description[l] || seo.description['zh'], 'property');
  setMeta('og:url', canonical, 'property');
  setMeta('og:locale', l === 'zh' ? 'zh_TW' : (l === 'ja' ? 'ja_JP' : 'en_US'), 'property');

  // Twitter
  setMeta('twitter:title', document.title);
  setMeta('twitter:description', seo.description[l] || seo.description['zh']);

  // hreflang
  updateHreflang(routePath);
}

/**
 * Update SEO for product/tech pages.
 * @param {string} productId - Product key (e.g. 'collie', 'beaver')
 * @param {string} lang - Internal lang code ('zh', 'en', 'ja') or slug ('zh-tw')
 * @param {string} routePath - Route path WITHOUT lang prefix (e.g. '/tech/collie')
 */
function updateProductSEO(productId, lang, routePath) {
  const l = (lang === 'zh-tw' || lang === 'zh') ? 'zh' : lang;
  const slug = langToSlug(lang);
  const seo = PRODUCT_SEO[productId];

  if (!seo) {
    updateSEO('solutions', lang, routePath);
    return;
  }

  // HTML lang attribute
  var htmlLang = l === 'zh' ? 'zh-TW' : l;
  document.documentElement.setAttribute('lang', htmlLang);

  document.title = seo.title[l] || seo.title['zh'];
  setMeta('description', seo.description[l] || seo.description['zh']);

  // Canonical — always uses correct slug in URL
  const canonical = BASE_URL + '/' + slug + routePath;
  setLink('canonical', canonical);

  setMeta('og:title', document.title, 'property');
  setMeta('og:description', seo.description[l] || seo.description['zh'], 'property');
  setMeta('og:url', canonical, 'property');
  setMeta('og:locale', l === 'zh' ? 'zh_TW' : (l === 'ja' ? 'ja_JP' : 'en_US'), 'property');

  setMeta('twitter:title', document.title);
  setMeta('twitter:description', seo.description[l] || seo.description['zh']);

  updateHreflang(routePath);
}

// --- Helpers ---

function setMeta(name, content, attr) {
  attr = attr || 'name';
  var el = document.querySelector('meta[' + attr + '="' + name + '"]');
  if (el) {
    el.setAttribute('content', content);
  } else {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    el.setAttribute('content', content);
    document.head.appendChild(el);
  }
}

function setLink(rel, href) {
  var el = document.querySelector('link[rel="' + rel + '"]');
  if (el) {
    el.setAttribute('href', href);
  }
}

function updateHreflang(pagePath) {
  var langs = { 'zh-TW': 'zh-tw', 'en': 'en', 'ja': 'ja' };
  Object.keys(langs).forEach(function(hreflang) {
    var el = document.querySelector('link[hreflang="' + hreflang + '"]');
    if (el) {
      el.setAttribute('href', BASE_URL + '/' + langs[hreflang] + pagePath);
    }
  });
  var def = document.querySelector('link[hreflang="x-default"]');
  if (def) {
    def.setAttribute('href', BASE_URL + '/zh-tw' + pagePath);
  }
}

export { updateSEO, updateProductSEO };
